import React, {useEffect, useState} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {Typography, Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import CreationFlowBar from '../../components/CreationFlowBar';
import Step1 from './steps/Step1';
import Step2 from './steps/Step2/Step2';
import {
    BackupOutlined,
    CheckCircleOutlineRounded,
    IntegrationInstructionsOutlined,
    PendingActions,
    PollOutlined
} from '@mui/icons-material';
import Step3 from './steps/step3/Step3Index';
import Step4 from './steps/step4/Step4';
import Step5 from './steps/Step5';
import {useAuth0} from '@auth0/auth0-react';
import {getAppConfig, useTrovoConfig} from '../../utils/config';
import BannerMessage from '../AccountDetails/BannerMessage';
import {Loading} from '../../components/Loading';
import Popup from '../../components/popup';
import {SamplesProgressModal} from './SamplesProgModal';
import {trimKeysAndValuesInObject} from '../../utils/helpers';
import {uploadFileMultiPart} from '../../utils/file-upload';
import {fileToBffEntry} from '../../utils/helpers';

const ExpContainer = styled(Box)({
    display: 'flex',
    margin: '0 auto',
    width: 'fit-content',
    paddingTop: '48px',
    gap: '20px'
});

const FormContainer = styled(Box)({
    // flex: 5,
    display: 'flex',
    flexDirection: 'column',
    width: '990px',
    gap: 10,
    marginBottom: '55px'
});

const formDataInitialState = {
    experiment_details: {
        name: '',
        description: '',
        organism: '',
        sequencing_type: ''
    },
    sequencing_details: {
        analyzed_molecule: '',
        rna_selection_method: '',
        sequencing_adapter: '',
        sequencing_platform: '',
        platform_model: '',
        sequencing_read_type: '',
        sequencing_sense: '',
        files_per_sample: ''
    },
    step_three_details: {},
    step_four_details: {},
    archived: false
};

const fileUploadProgressInitialState: FileUploadProgress = {
    processing: false,
    createSamplesDone: false,
    getPresignedUrlsDone: false,
    uploadFilesDone: false,
    uploadedFiles: 0,
    totalFiles: 0,
    uploadedSize: 0,
    totalSize: 0,
    uploadStartedAt: new Date()
};

/**
 * Checks if the sequencing details are complete. Completeness means all
 * choices have a value and depends on the sequencing type.
 *
 * This is a copy of isCompleteSequencingDetails in Step2.tsx, HOWEVER, we
 * cannot check sequencing_sense here. Unstranded is represented as '' and will
 * thus also fail the check. Hence the need to make a near identical copy. In
 * Step2 this is not a problem since we explicitly use 'Unstranded'. The change
 * cannot easily be made here since it affects what is stored in the database,
 * which in turn has further consequences downstream.
 *
 * @param experimentData
 * @return True if the sequencing details are complete for the sequencing_type.
 *         False otherwise or if unknown sequencing_type.
 */
const isCompleteSequencingDetails = (experimentData: any) => {
    if (['ChIP Seq', 'ATAC Seq'].includes(experimentData.sequencing_type)) {
        return Boolean(
            experimentData.sequencing_platform &&
                experimentData.platform_model &&
                experimentData.sequencing_read_type &&
                experimentData.files_per_sample
        );
    } else if (experimentData.sequencing_type === 'RNA Seq') {
        return Boolean(
            experimentData.analyzed_molecule &&
                experimentData.rna_selection_method &&
                experimentData.sequencing_adapter &&
                experimentData.sequencing_platform &&
                experimentData.platform_model &&
                experimentData.sequencing_read_type &&
                experimentData.files_per_sample
        );
    } else {
        return false;
    }
};

function CreateExperiment() {
    const {getAccessTokenSilently} = useAuth0();
    const {apiHost} = getAppConfig();
    const {id} = useParams();
    const navigate = useNavigate();
    const {user} = useTrovoConfig();
    const [samples, setSamples] = useState([] as any[]);
    const [step, setStep] = useState(0);
    const [experiment, setExperiment] = useState<any>(null);
    const [fileUploadProgress, setFileUploadProgress] = useState(fileUploadProgressInitialState);
    const [expectedFilenamesBySampleId, setExpectedFilenamesBySampleId] = useState<Record<string, string[]>>({});
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState<{
        experiment_details: ExperimentDetails;
        sequencing_details: SequencingDetails;
        step_three_details: StepThreeDetails;
        step_four_details: StepFourDetails;
    }>(formDataInitialState);

    /**
     * Calls the BFF `/experiment/:experiment_id/sample` endpoint to fetch the
     * samples for the current experiment. Results are set to the samples
     * state.
     */
    const fetchSamples = async () => {
        if (!id) return;
        try {
            const response = await fetch(`${apiHost}/experiment/${id}/sample`, {
                headers: {
                    Authorization: `Bearer ${await getAccessTokenSilently()}`
                }
            });
            const samples: any[] = await response.json();
            samples.sort((a, b) => new Date(a.created_time).getTime() - new Date(b.created_time).getTime());
            setSamples(samples);
        } catch (err: any) {
            console.error(err.message || 'An error occurred.');
        }
    };

    /**
     * If an experiment ID is present, it means the user is continuing a draft
     * experiment. This function fetches the experiment data from the API and
     * sets the state of the form, the steps, and the experiment to match what
     * was already saved.
     *
     * Uses `setExperiment`, `setFormData`, `setStep` (indirectly), and
     * `setLoading`.
     */
    const fetchData = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await fetch(`${apiHost}/experiment/${id}`, {
                headers: {
                    Authorization: `Bearer ${await getAccessTokenSilently()}`
                }
            });
            const data = await response.json();
            if (data?.pending === false) {
                navigate(`/experiment/${id}`, {replace: true});
                return;
            }
            determineStepBasedOnData(data);
            setExperiment(data);
            const {
                name,
                description,
                organism,
                sequencing_type,
                analyzed_molecule,
                rna_selection_method,
                sequencing_adapter,
                sequencing_platform,
                platform_model,
                sequencing_read_type,
                sequencing_sense,
                files_per_sample,
                sample_count
            } = data;

            setFormData({
                experiment_details: {name, description, organism, sequencing_type},
                sequencing_details: {
                    analyzed_molecule,
                    rna_selection_method,
                    sequencing_adapter,
                    sequencing_platform,
                    platform_model,
                    sequencing_read_type,
                    sequencing_sense,
                    files_per_sample
                },
                step_three_details: {},
                step_four_details: {}
            });
        } catch (err: any) {
            console.error(err.message || 'An error occurred.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Sets step state based on whether certain properties of the given data
     * have a truthy value.
     *
     * Uses `setStep`.
     */
    const determineStepBasedOnData = (data: any) => {
        if (!data.name || !data.organism || !data.sequencing_type) {
            setStep(0);
        } else if (!isCompleteSequencingDetails(data)) {
            setStep(1);
        } else if (!data.sample_count || data.sample_count === '0') {
            setStep(2);
        } else {
            setStep(3);
        }
    };

    // Adding `id` as a dependency to ensure the experiment variable is set
    // correctly after the initial draft is saved. This will show the loading
    // indicator while it fetches that data after the first save is pressed.
    // For all other steps, the saving should handle updating the experiment
    // value. At the time of writing, this is done by calling fetchData in
    // handleSave.
    useEffect(() => {
        fetchData();
    }, [id]);

    useEffect(() => {
        fetchSamples();
    }, [step]);

    /**
     * Saves current experiment details to the BFF. Creates new experiment if
     * there was no `id` yet (and redirects to a url with an `id` if so).
     * Updates existing experiment if there was an `id`.
     *
     * Updates step state or redirects to home.
     */
    const handleSave = async (
        whatToSave: ExperimentDetails | SequencingDetails | null | {pending: false}
    ): Promise<{error: string} | void> => {
        if (whatToSave) {
            const isFinalSubmission = step === 4 && 'pending' in whatToSave && whatToSave.pending === false;
            try {
                const body = id ? {...whatToSave} : {...whatToSave, archived: false, pending: true, group_options: {}};
                const endpoint = id ? `${apiHost}/experiment/${id}` : `${apiHost}/experiment/create`;
                const method = id ? 'PUT' : 'POST';
                const response = await fetch(endpoint, {
                    method: method,
                    headers: {
                        Authorization: `Bearer ${await getAccessTokenSilently()}`,
                        Accept: 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(body)
                });
                const responseData = await response.json();
                if (response.ok) {
                    if (!id) {
                        navigate(`/experiment/draft/${responseData.id}`);
                    } else if (!isFinalSubmission) {
                        // skip this fetch on final submit due to race condition:
                        // once pending is false, the draft route guard in fetchData
                        // can redirect before (chip) redirect to /experiment/${id}/analysis/draft/
                        // also, there is no need to fetch data at final submission
                        // since we are redirecting either way

                        // fetchData relies on `id` being set. Ensures state of
                        // the experiment is up to date after making changes.
                        // Ideally we would just use the result from the
                        // POST/PUT response to set the experiment, but the Bff
                        // returns extra info in the GET that the application
                        // needs (specifically the sample count).
                        fetchData();
                    }
                } else {
                    console.error(responseData.error || 'An error occurred.');
                    return {error: responseData.error};
                }
            } catch (err: any) {
                console.error(err.message || 'An error occurred.');
                return {error: err.message};
            }
        }

        if (step === 4) {
            if (experiment?.sequencing_type === 'ChIP Seq') {
                // If ChIP Seq, redirect to create default analysis.
                navigate(`/experiment/${id}/analysis/draft/`);
            } else {
                navigate('/');
            }
        } else {
            setStep(step + 1);
        }
    };

    /**
     * Creates samples in the BFF.
     *
     * @param userinputSamples from their CSV: objects with 'SampleName'. For RNA also other categories, for ChIP things like Control, etc.
     */
    const createSamples = async (
        userinputSamples: any[],
        samplenameToFiles: Record<string, File[]>,
        samplenameToExpectedFilenames: Record<string, string[]> = {}
    ) => {
        let totalFiles = 0;
        let totalSize = 0;
        // Reset file upload progress state. Need to reset in case the user
        // navigates back to the bulk upload page (without a page refresh or
        // anything) and uses it to create a fresh set of samples.
        setFileUploadProgress({...fileUploadProgressInitialState, processing: true});

        // Create samples and file info in the BFF. End up with created samples.
        const samplesPromises = userinputSamples.map((sample: any) => {
            return createSample(sample, samplenameToFiles[sample.SampleName] || []);
        });
        const responses = await Promise.all(samplesPromises);
        const samples = await Promise.all(responses.map(response => response.json()));
        // after samples created, saves {sample id: [file names]}, used by step4 to know expected filenames by sample id
        const sampleIdToExpectedFilenames: Record<string, string[]> = {};
        for (const sample of samples) {
            sampleIdToExpectedFilenames[sample.id] = samplenameToExpectedFilenames[sample.name] || [];
        }
        setExpectedFilenamesBySampleId(sampleIdToExpectedFilenames);

        setFileUploadProgress(state => ({...state, createSamplesDone: true, uploadStartedAt: new Date()}));

        const apiAccessToken = await getAccessTokenSilently();
        // Initial loop to get total count of files to upload and the
        // required metadata. Will be used for progress indicator.
        const toUpload: {sampleId: string; fileId: string; fileObject: File}[] = [];
        for (const sample of samples) {
            // files are what is returned from the BFF (DB), need to connect
            // the file db ID to the File objects
            const files = sample.files || [];
            for (const file of files) {
                const fileObj: File | undefined = samplenameToFiles[sample.name]?.find((ff: File) => ff.name === file.name);
                if (fileObj) {
                    totalFiles++;
                    totalSize += fileObj.size;
                    toUpload.push({sampleId: sample.id, fileId: file.id, fileObject: fileObj});
                }
            }
        }
        setFileUploadProgress(state => ({...state, totalFiles, totalSize}));

        // Second loop to upload files
        for (const {sampleId, fileId, fileObject} of toUpload) {
            try {
                await uploadFileMultiPart(apiHost, apiAccessToken, id!, sampleId, fileId, fileObject, {
                    onChunkSuccess: (_partNumber, partSize) => {
                        // Can use partNumber for some fancy progress bar?
                        // Would need to add a hook for total number of parts.
                        setFileUploadProgress(state => ({
                            ...state,
                            uploadedSize: state.uploadedSize + partSize
                        }));
                    },
                    onChunkRetry: (partNumber, attempts) => {
                        console.warn(`Retrying part ${partNumber} for the ${attempts} time.`);
                    },
                    onChunkError: (partNumber, error) => {
                        console.error(`Failed to upload part ${partNumber}: ${error.message}`);
                    }
                });
                setFileUploadProgress(state => ({
                    ...state,
                    uploadedFiles: state.uploadedFiles + 1
                }));
            } catch (err: unknown) {
                console.error(`Failed to upload file ${fileObject.name}: ${(err as Error).message}`);
            }
        }

        // TODO: Actually do something when a file is not uploaded? At least for now, the next page will notice when validate-file-upload fails.

        // Refresh state of the form by fetching all samples from DB.
        // Also refresh the experiment state to get the updated sample count.
        await Promise.all([fetchSamples(), fetchData()]);

        setFileUploadProgress(state => ({...state, processing: false, uploadFilesDone: true}));
        setStep(3);
    };

    /**
     * Creates a single sample in the BFF. Expects the uploading file objects
     * too to get their sizes.
     */
    const createSample = async (sample: any, fileObjects: File[]): Promise<Response> => {
        // If I understand correctly, 'Filename' should not even be a key at
        // this point? Not removing the mention just in case it is. Keep it out
        // of other_categories in that case.
        const {SampleName: sampleName, Filename: filename, ...other_categories} = sample;
        const otherCategoriesTrimmed = trimKeysAndValuesInObject(other_categories);
        const fileObjectsToInsert = fileObjects.map((f: File) =>
            fileToBffEntry(
                f,
                formData.sequencing_details.sequencing_sense,
                formData.sequencing_details.sequencing_read_type && formData.sequencing_details.sequencing_read_type.toLowerCase()
            )
        );

        const sampleRecord = {
            name: sampleName,
            organism: formData.experiment_details.organism,
            analyzed_molecule: formData.sequencing_details.analyzed_molecule,
            sequencing_instrument_platform: formData.sequencing_details.sequencing_platform,
            sequencing_instrument_model: formData.sequencing_details.platform_model,
            experiment_id: id,
            other_categories: otherCategoriesTrimmed,
            files: fileObjectsToInsert
        };
        return fetch(`${apiHost}/experiment/${id}/sample/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${await getAccessTokenSilently()}`
            },
            body: JSON.stringify(sampleRecord)
        });
    };

    const steps = [
        {
            name: 'Enter Experiment Details',
            caption: 'Record your experiment name, description, and other identifying information.',
            description:
                'Record the identifying details of your experiment. You can use this information to create a unique identifier for your experiment, such as “Experiment 1: Transcriptome of analysis of human heart failure.” We recommend using clear, descriptive naming conventions to help you organize and differentiate between your experiments. ',
            component: (
                <Step1
                    formData={formData}
                    setExperimentDetails={(obj: ExperimentDetails) => {
                        setFormData(state => ({...state, experiment_details: obj}));
                        return handleSave(obj);
                    }}
                />
            ),
            icon: <PendingActions sx={{fill: step === 0 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Enter Sequencing Details',
            caption: 'Enter required information about your data files.',
            description:
                'Select the configuration that corresponds with your data files as provided by the organization that generated them.',
            component: (
                <Step2
                    formData={formData}
                    setSequencingDetails={(obj: SequencingDetails) => {
                        setFormData(state => ({...state, sequencing_details: obj}));
                        handleSave(obj);
                    }}
                />
            ),
            icon: <PollOutlined sx={{fill: step === 1 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Build Your Experiment',
            caption: 'Format and upload your sample information.',
            component: <Step3 experiment={experiment} createSamples={createSamples} samples={samples} setStep={setStep} />,
            icon: <BackupOutlined sx={{fill: step === 2 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Edit Samples',
            caption: 'Review and edit the labels of sample variables and values.',
            description:
                'Make global changes to the variable and value labels you are using throughout this experiment. Changes to these labels will appear in any sample to which they are assigned.',
            component: (
                <Step4
                    experiment={experiment}
                    setFormData={setFormData}
                    setStep={setStep}
                    expectedFilenamesBySampleId={expectedFilenamesBySampleId}
                />
            ),
            icon: <IntegrationInstructionsOutlined sx={{fill: step === 3 ? 'url(#linearColors)' : ''}} />
        },
        {
            name: 'Review & Create',
            caption: 'Check your work before finalizing this experiment.',
            component: (
                <Step5
                    experiment={experiment}
                    formData={formData}
                    samples={samples}
                    setStep={setStep}
                    submitExperiment={() => handleSave({pending: false})}
                />
            ),
            icon: <CheckCircleOutlineRounded sx={{fill: step === 4 ? 'url(#linearColors)' : ''}} />
        }
    ];

    if (loading) {
        return (
            <Box>
                <Loading />
            </Box>
        );
    }

    if (fileUploadProgress.processing) {
        return (
            <Box>
                <Popup isOpen={fileUploadProgress.processing}>
                    <SamplesProgressModal fileUploadProgress={fileUploadProgress} />
                </Popup>
            </Box>
        );
    }

    return (
        <Box>
            <ExpContainer>
                <CreationFlowBar title="CREATE NEW EXPERIMENT" steps={steps} step={step} setStep={setStep} />

                <FormContainer>
                    <Box sx={{marginBottom: '40px'}}>
                        <BannerMessage
                            show={user.cb_item_price_id?.includes('demo')}
                            setHide={() => null}
                            showClose={false}
                            title="Upgrade to a paid plan to unlock all features."
                            message="You are currently using a demo account."
                        />
                    </Box>
                    <Typography variant="headline" size="large">
                        {steps[step].name}
                    </Typography>
                    {experiment?.sequencing_type && (
                        <Typography variant="body" size="small" sx={{mb: 1}}>
                            Experiment Type: {experiment?.sequencing_type}
                        </Typography>
                    )}
                    <Typography variant="body" size="medium" mb={2}>
                        {steps[step].description}
                    </Typography>
                    <div
                        style={{
                            margin: '2px 0',
                            padding: step !== 3 ? 20 : 0,
                            background: step !== 3 ? 'white' : '',
                            borderRadius: 10
                        }}>
                        {steps[step].component}
                    </div>
                </FormContainer>
            </ExpContainer>
        </Box>
    );
}

export default CreateExperiment;
